import { api } from './axios';

/**
 * Quick Duel API — Быстрый поиск матчей (Quick Match)
 * @see api_documentation/duel/quick_duel_api.md
 */

/**
 * @typedef {'SOLO' | 'TEAM_2V2' | 'TEAM_3V3' | 'ANY'} QuickDuelFormat
 */

/**
 * @typedef {'SINGLE_ROUND' | 'BEST_OF_THREE'} DuelRoundType
 */

/**
 * @typedef {'WAITING' | 'MATCHED' | 'CANCELLED' | 'EXPIRED' | 'MATCH_FAILED'} QueueStatus
 */

/**
 * @typedef {Object} JoinQuickQueueRequest
 * @property {QuickDuelFormat} preferredFormat - Предпочтительный формат
 * @property {DuelRoundType} preferredRoundType - Предпочтительный тип раундов
 * @property {number} [minRating] - Мин. рейтинг соперника
 * @property {number} [maxRating] - Макс. рейтинг соперника
 * @property {string} [roundContentType] - Тип контента: AUDIO, TEXT, HYBRID
 * @property {boolean} [allowHigherRating] - Разрешить соперников с рейтингом выше
 */

/**
 * @typedef {Object} QuickQueueStatusResponse
 * @property {string} queueId - ID очереди
 * @property {QueueStatus} status - Статус очереди
 * @property {QuickDuelFormat} preferredFormat - Предпочтительный формат
 * @property {DuelRoundType} preferredRoundType - Предпочтительный тип раундов
 * @property {number} playerRating - Рейтинг игрока
 * @property {number} ratingRange - Диапазон рейтинга
 * @property {number} estimatedWaitSeconds - Примерное время ожидания в секундах
 * @property {string} searchStartedAt - Время начала поиска (ISO 8601)
 * @property {string|null} [matchedAt] - Время нахождения матча
 * @property {string|null} [duelId] - ID дуэли при нахождении матча
 */

/**
 * @typedef {Object} QuickQueueStatsResponse
 * @property {number} activeQueues - Активные очереди
 * @property {number} soloQueues - Соло очереди
 * @property {number} team2v2Queues - Командные очереди 2v2
 * @property {number} averageWaitSeconds - Среднее время ожидания
 * @property {number|null} [yourPosition] - Позиция в очереди (если есть)
 */

/**
 * Войти в очередь поиска быстрой дуэли
 * @param {JoinQuickQueueRequest} data - Параметры поиска
 * @returns {Promise<QuickQueueStatusResponse>} Статус в очереди
 */
export const joinQueue = (data) => {
    return api.post('/duels/quick/join', data).then((res) => res.data);
};

/**
 * Выйти из очереди поиска матча
 * @returns {Promise<void>}
 */
export const leaveQueue = () => {
    return api.delete('/duels/quick/leave').then((res) => res.data);
};

/**
 * Отменить поиск быстрой дуэли
 * @returns {Promise<void>}
 */
export const cancelQueue = () => {
    return api.post('/duels/quick/cancel').then((res) => res.data);
};

/**
 * Получить текущий статус в очереди
 * @returns {Promise<QuickQueueStatusResponse>} Статус очереди
 */
export const getQueueStatus = () => {
    return api.get('/duels/quick/status').then((res) => res.data);
};

/**
 * Получить статистику очереди быстрых дуэлей
 * @returns {Promise<QuickQueueStatsResponse>} Статистика очереди
 */
export const getQueueStats = () => {
    return api.get('/duels/quick/stats').then((res) => res.data);
};

/**
 * Принять найденный матч
 * @param {string} duelId - ID дуэли
 * @returns {Promise<Object>} Результат принятия
 */
export const acceptMatch = (duelId) => {
    return api.post('/duels/quick/accept', { duelId, accepted: true }).then((res) => res.data);
};

/**
 * Отклонить найденный матч
 * @param {string} duelId - ID дуэли
 * @returns {Promise<Object>} Результат отклонения
 */
export const declineMatch = (duelId) => {
    return api.post('/duels/quick/decline', { duelId }).then((res) => res.data);
};

export default {
    joinQueue,
    leaveQueue,
    cancelQueue,
    getQueueStatus,
    getQueueStats,
    acceptMatch,
    declineMatch
};
